import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Loader2, Plus, Clock, ArrowUp, ArrowDown, Edit2 } from "lucide-react";
import Button from "../../common/Button";
import NoDataFound from "../../common/NoDataFound";
import {
    governanceProcessStepsService,
    sopVersioningService,
    type ProcessStep,
    type StepAuthority,
} from "../../../services/governanceService";
import { extractApiError } from "../../../utils/apiError";

interface Props {
    processId: number;
    /** When set, the panel shows and reorders the steps of this draft of the
        SOP's next version instead of the live steps. */
    draftId?: number;
    /** SOP code e.g. "SOP-001" — used to render sub-process codes like SOP-001.002 */
    sopCode?: string;
    /** Live tab: read-only timeline. Draft tab: same timeline + Add Sub-process
        button + per-card pencil for inline edit. */
    readOnly?: boolean;
    /** Replaces the default read-only note (e.g. for a draft awaiting approval). */
    readOnlyNote?: string;
    onCreateStep?: () => void;
    /** Extra action(s) rendered in the header row, left of "Add Sub-process"
        (e.g. the Draft SOP "Roll it out into a new version" button). */
    headerAction?: React.ReactNode;
    /** When provided, card body click calls this instead of the internal
        3-column modal. Used by Draft SOP to open the parent config modal. */
    onStepClick?: (step: ProcessStep) => void;
    /** When provided, shows a pencil icon on each card that triggers edit mode
        instead of the configure drill-down. */
    onEditStep?: (step: ProcessStep) => void;
}

export interface SopStepsPanelHandle {
    reload: () => Promise<void>;
}

// Role drives chip color, verb label, and left-border accent on the card.
const ROLE_COLOR: Record<string, string> = {
    PERFORM: "bg-emerald-50 text-emerald-700 border-emerald-100",
    APPROVE: "bg-purple-50 text-purple-700 border-purple-100",
    REVIEW:  "bg-amber-50 text-amber-700 border-amber-100",
    NOTIFY:  "bg-blue-50 text-blue-700 border-blue-100",
    OTHER:   "bg-gray-100 text-gray-700 border-gray-200",
};

const ROLE_BORDER: Record<string, string> = {
    PERFORM: "border-l-emerald-400",
    APPROVE: "border-l-purple-400",
    REVIEW:  "border-l-amber-400",
    NOTIFY:  "border-l-blue-400",
    OTHER:   "border-l-gray-300",
};

const ROLE_VERB: Record<string, string> = {
    PERFORM: "performs",
    APPROVE: "approves",
    REVIEW:  "reviews",
    NOTIFY:  "is notified",
    OTHER:   "",
};

const formatTimeline = (s: ProcessStep): string | null => {
    if (s.timeline_days == null) return null;
    const days = s.timeline_days;
    const noun = days === 1 ? "day" : "days";
    switch (s.timeline_anchor) {
        case "AFTER_PREV_END":   return `+${days} ${noun} after previous step ends`;
        case "AFTER_PREV_START": return `+${days} ${noun} after previous step starts`;
        case "FIXED":            return `+${days} fixed calendar ${noun}`;
        default:                 return `+${days} ${noun}`;
    }
};

const SopStepsPanel = forwardRef<SopStepsPanelHandle, Props>(({ processId, draftId, sopCode, readOnly, readOnlyNote, onCreateStep, headerAction, onStepClick, onEditStep }, ref) => {
    const [steps, setSteps] = useState<ProcessStep[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = draftId
                ? await sopVersioningService.getDraftSteps(draftId)
                : await governanceProcessStepsService.getByProcess(processId);
            setSteps(list);
        } catch (e: any) {
            setError(extractApiError(e, "Failed to load steps."));
        } finally {
            setLoading(false);
        }
    }, [processId, draftId]);

    useEffect(() => { load(); }, [load]);

    useImperativeHandle(ref, () => ({ reload: load }), [load]);

    // Move a step up or down in the order, then persist the new sequence in
    // a single PATCH call. The panel re-renders immediately for a snappy
    // feel; if the server rejects we reload and show the toast.
    const moveStep = async (idx: number, direction: -1 | 1) => {
        const target = idx + direction;
        if (target < 0 || target >= steps.length) return;
        const reordered = steps.slice();
        const [moved] = reordered.splice(idx, 1);
        reordered.splice(target, 0, moved);
        // Optimistic re-render with placeholder numbers (final values come back from server).
        setSteps(reordered.map((s, i) => ({ ...s, step_number: i + 1 })));
        try {
            const order = reordered.map((s, i) => ({ step_id: s.id, step_number: i + 1 }));
            if (draftId) await sopVersioningService.reorderDraftSteps(draftId, order);
            else await governanceProcessStepsService.reorder(processId, order);
            await load();
        } catch (e: any) {
            setError(extractApiError(e, "Failed to reorder steps."));
            await load();
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin mr-2" size={16} /> Loading steps…
            </div>
        );
    }
    if (error) return <div className="p-3 text-sm text-red-600">{error}</div>;

    return (
        <div className="flex flex-col gap-y-3">
            {readOnly && (
                <div className="text-[11px] uppercase tracking-wider text-amber-700">
                    {readOnlyNote || "Read-only — open the Draft SOP tab to edit steps."}
                </div>
            )}
            {readOnly && headerAction && (
                <div className="flex justify-end items-center gap-2">{headerAction}</div>
            )}
            {!readOnly && (onCreateStep || headerAction) && (
                <div className="flex justify-end items-center gap-2">
                    {headerAction}
                    {onCreateStep && (
                        <Button variant="fill" icon={Plus} onClick={onCreateStep} data-testid="BTN_sop_steps_create">
                            Add Sub-process
                        </Button>
                    )}
                </div>
            )}

            {steps.length === 0 ? (
                <NoDataFound
                    title="No Sub-processes Yet"
                    message={!readOnly && onCreateStep
                        ? 'Click "Add Sub-process" to add one.'
                        : 'No sub-processes have been defined yet.'}
                />
            ) : (
                <ol className="relative ml-3 pl-6 border-l-2 border-gray-200 flex flex-col gap-y-4">
                    {steps.map((s, idx) => (
                        <StepCard
                            key={s.id}
                            step={s}
                            sopCode={sopCode}
                            readOnly={readOnly}
                            onOpen={onStepClick ? () => onStepClick(s) : undefined}
                            onEdit={onEditStep ? () => onEditStep(s) : undefined}
                            onMoveUp={idx > 0 ? () => moveStep(idx, -1) : undefined}
                            onMoveDown={idx < steps.length - 1 ? () => moveStep(idx, 1) : undefined}
                        />
                    ))}
                </ol>
            )}
        </div>
    );
});

SopStepsPanel.displayName = "SopStepsPanel";

const StepCard: React.FC<{
    step: ProcessStep;
    sopCode?: string;
    readOnly?: boolean;
    onOpen?: () => void;
    onEdit?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}> = ({ step, sopCode, readOnly, onOpen, onEdit, onMoveUp, onMoveDown }) => {
    const timeline = formatTimeline(step);
    const authorities = step.authorities || [];
    // Left-border accent keyed to the first authority's role (or neutral if none).
    const primaryRole = authorities[0]?.role || "OTHER";
    const accentBorder = ROLE_BORDER[primaryRole] || ROLE_BORDER.OTHER;

    return (
        <li className="relative">
            {/* Workflow dot anchored on the vertical rail */}
            <span
                className="absolute -left-[33px] top-3 w-4 h-4 rounded-full bg-white border-2 border-emerald-400 flex items-center justify-center"
                aria-hidden
            >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            </span>

            <div
                role={onOpen ? "button" : undefined}
                tabIndex={onOpen ? 0 : undefined}
                onClick={onOpen}
                onKeyDown={onOpen ? (e) => { if (e.key === "Enter" || e.key === " ") onOpen(); } : undefined}
                className={`block border border-gray-200 border-l-4 ${accentBorder} rounded-lg bg-white transition${onOpen ? " hover:shadow-sm cursor-pointer" : ""}`}
                data-testid={`BTN_sop_steps_row_${step.id}_open`}
            >
                <div className="px-4 py-3 flex flex-col gap-y-2">
                    {/* Top row: step number + title + actions (no type badge — roles are on chips) */}
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            {sopCode ? `${sopCode}.${String(step.step_number).padStart(3, '0')}` : `STEP ${step.step_number}`}
                        </span>
                        <span className="text-sm font-semibold text-gray-800 flex-1">
                            {step.step_name}
                            {step.draft_id != null && step.live_step_id == null && (
                                <span className="status-badge sm info" style={{ marginLeft: 8 }}>New in this version</span>
                            )}
                            {step.is_active === 0 && (
                                <span className="status-badge sm" style={{ marginLeft: 8 }}>Inactive</span>
                            )}
                        </span>
                        {/* Reorder + Edit live only on the Draft tab. Each
                            uses stopPropagation so the card's row-click stays
                            clean (opens the 3-column drill-down). */}
                        {!readOnly && onEdit && (
                            <button
                                type="button"
                                className="btn-primary-outline-none app-modal-header-icon"
                                title="Edit sub-process"
                                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                                data-testid={`BTN_sop_steps_row_${step.id}_edit`}
                            >
                                <Edit2 size={14} />
                            </button>
                        )}
                        {!readOnly && (onMoveUp || onMoveDown) && (
                            <>
                                <button
                                    type="button"
                                    className="btn-primary-outline-none app-modal-header-icon"
                                    title="Move up"
                                    disabled={!onMoveUp}
                                    style={onMoveUp ? undefined : { opacity: 0.3, cursor: "not-allowed" }}
                                    onClick={(e) => { e.stopPropagation(); onMoveUp?.(); }}
                                    data-testid={`BTN_sop_steps_row_${step.id}_move_up`}
                                >
                                    <ArrowUp size={14} />
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary-outline-none app-modal-header-icon"
                                    title="Move down"
                                    disabled={!onMoveDown}
                                    style={onMoveDown ? undefined : { opacity: 0.3, cursor: "not-allowed" }}
                                    onClick={(e) => { e.stopPropagation(); onMoveDown?.(); }}
                                    data-testid={`BTN_sop_steps_row_${step.id}_move_down`}
                                >
                                    <ArrowDown size={14} />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Timeline */}
                    {timeline && (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            <Clock size={11} />
                            <span>{timeline}</span>
                            {step.after_step_name && (
                                <span className="text-gray-400">· after "{step.after_step_name}"</span>
                            )}
                        </div>
                    )}

                    {/* Long description */}
                    {step.description && (
                        <div className="text-xs text-gray-600 whitespace-pre-line">{step.description}</div>
                    )}

                    {/* Authority chips — one per entry in the sub-table. */}
                    {authorities.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {authorities.map((a: StepAuthority) => {
                                const chipColor = ROLE_COLOR[a.role] || ROLE_COLOR.OTHER;
                                const verb = ROLE_VERB[a.role] || "";
                                const name = a.authority_name || `Authority #${a.authority_id}`;
                                return (
                                    <span
                                        key={a.id}
                                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${chipColor}`}
                                    >
                                        <span className="text-gray-800">{name}</span>
                                        {verb && <span className="text-gray-400">— {verb}</span>}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
};

export default SopStepsPanel;
